<template>
  <article class="video-card" :class="{'video-card--read': isInHistory}">
    <!-- Miniatura del video -->
    <div class="video-card__thumbnail" @click="openLink">
      <img
        v-if="item.image"
        :src="item.image"
        :alt="item.title"
        @error="onImageError"
        class="thumbnail-image"
        crossorigin="anonymous"
      />
      <div v-else class="thumbnail-placeholder">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
      <!-- Ícono de play según plataforma -->
      <div class="video-play-icon" :class="{'video-play-icon--tiktok': item.sourceType === 'tiktok'}">
        <svg v-if="item.sourceType === 'tiktok'" width="48" height="48" viewBox="0 0 24 24" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
        <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="white">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    </div>

    <!-- Contenido del card -->
    <div class="video-card__content">
      <h3 class="video-card__title" @click="openLink">{{ item.title }}</h3>
      <p class="video-card__category">{{ item.sourceTitle || item.sourceCategory || item.category }}</p>
      <p class="video-card__description" v-if="item.description">{{ truncateDescription(item.description) }}</p>
    </div>

    <!-- Metadata: fecha y acciones -->
    <div class="video-card__footer">
      <span class="footer-date">
        {{ timeAgo(item.pubDate) }}
        <span v-if="item.sourceCountry" class="footer-country"> • {{ item.sourceCountry }}</span>
      </span>
      <div class="footer-actions">
        <!-- Botón compartir -->
        <div class="share-btn-wrapper" v-if="!isModal">
          <button
            @click.stop="shareCard"
            class="action-btn"
            :class="{'action-btn--copied': showCopied}"
            :title="showCopied ? '¡Copiado!' : 'Compartir'"
          >
            <svg v-if="!showCopied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>

          <!-- Tooltip "Enlace copiado" -->
          <transition name="tooltip-fade">
            <div v-if="showCopied" class="share-tooltip">
              Enlace copiado
            </div>
          </transition>
        </div>

        <!-- Botón favorito -->
        <div class="favorite-btn-wrapper">
          <button
            @click.stop="toggleFavorite"
            class="action-btn"
            :class="{'action-btn--active': isFavorite}"
            :title="isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </button>

          <!-- Tooltip de favorito -->
          <transition name="tooltip-fade">
            <div v-if="showFavoriteTooltip" class="favorite-tooltip">
              {{ favoriteTooltipText }}
            </div>
          </transition>
        </div>

        <!-- Indicador de leído -->
        <button
          v-if="isInHistory"
          class="action-btn action-btn--active"
          title="Leído"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import { timeAgo } from '../../../utils/time.js';
import bookmarksService from '../../../services/bookmarks.js';
import { copyShareUrl } from '../../../utils/shareUtils.js';

export default {
  name: 'VideoCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      updateTrigger: 0,
      imageError: false,
      // Estado del botón compartir
      showCopied: false,
      // Estado del tooltip de favorito
      showFavoriteTooltip: false,
      favoriteTooltipText: ''
    };
  },
  mounted() {
    window.addEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  },
  computed: {
    isFavorite() {
      this.updateTrigger;
      return bookmarksService.isFavorite(this.item.id || this.item.link);
    },
    isInHistory() {
      this.updateTrigger;
      return bookmarksService.isInHistory(this.item.id || this.item.link);
    }
  },
  methods: {
    timeAgo,
    openLink() {
      const videoType = this.item.sourceType || 'youtube';
      let videoId = null;
      let isShort = false;

      if (videoType === 'tiktok') {
        videoId = this.extractTikTokId(this.item.link);
        isShort = true; // Todos los TikToks son shorts
      } else {
        videoId = this.extractYouTubeId(this.item.link);
        // Detectar si es YouTube Short
        isShort = this.item.isShortVideo || (this.item.link && this.item.link.includes('/shorts/'));
      }

      if (videoId) {
        if (isShort) {
          // Emitir evento para abrir reproductor de shorts
          this.$emit('play-short', videoId, this.item.title, videoType);
        } else {
          // Emitir evento para abrir reproductor normal de YouTube
          this.$emit('play-video', videoId, this.item.title);
        }
      } else {
        // Fallback: abrir en nueva pestaña
        window.open(this.item.link, '_blank', 'noopener,noreferrer');
      }

      // Agregar al historial
      bookmarksService.addToHistory(this.item);
    },
    extractYouTubeId(url) {
      if (!url) return null;

      // Para URLs normales: youtube.com/watch?v=VIDEO_ID
      if (url.includes('youtube.com/watch?v=')) {
        return url.split('v=')[1].split('&')[0];
      }

      // Para URLs cortas: youtu.be/VIDEO_ID
      if (url.includes('youtu.be/')) {
        return url.split('youtu.be/')[1].split('?')[0];
      }

      // Para shorts: youtube.com/shorts/VIDEO_ID
      if (url.includes('youtube.com/shorts/')) {
        return url.split('shorts/')[1].split('?')[0];
      }

      return null;
    },
    extractTikTokId(url) {
      if (!url) return null;

      // Formato: https://www.tiktok.com/@usuario/video/ID
      if (url.includes('tiktok.com') && url.includes('/video/')) {
        const parts = url.split('/video/');
        if (parts[1]) {
          return parts[1].split('?')[0];
        }
      }

      return null;
    },
    toggleFavorite() {
      if (this.isFavorite) {
        bookmarksService.removeFromFavorites(this.item.id || this.item.link);
        this.favoriteTooltipText = 'Quitado de favoritos';
      } else {
        bookmarksService.addToFavorites(this.item);
        this.favoriteTooltipText = 'Agregado a favoritos';
      }

      // Mostrar tooltip
      this.showFavoriteTooltip = true;
      setTimeout(() => {
        this.showFavoriteTooltip = false;
      }, 2000);
    },
    async shareCard() {
      const success = await copyShareUrl(this.item);

      if (success) {
        // Mostrar feedback visual
        this.showCopied = true;
        setTimeout(() => {
          this.showCopied = false;
        }, 2000);
      } else {
        console.error('Failed to copy share URL');
      }
    },
    onImageError(event) {
      this.imageError = true;
      event.target.style.display = 'none';
    },
    truncateDescription(text) {
      if (!text) return '';
      const maxLength = 120;
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    handleBookmarkUpdate() {
      this.updateTrigger++;
    }
  },
  beforeUnmount() {
    window.removeEventListener('bookmarks-updated', this.handleBookmarkUpdate);
  }
};
</script>

<style scoped>
/* Card de video estilo YouTube */
.video-card {
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 420px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  border-color: #ff0000; /* Rojo para YouTube por defecto */
}

/* Hover para cards de TikTok */
.video-card:hover:has(.video-play-icon--tiktok) {
  border-color: #ffffff; /* Blanco para TikTok */
}

/* Miniatura del video */
.video-card__thumbnail {
  width: 100%;
  height: 200px;
  position: relative;
  overflow: hidden;
  background: #000;
  cursor: pointer;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.video-card:hover .thumbnail-image {
  transform: scale(1.05);
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #262c34, #1c2127);
  color: #4a5568;
}

/* Ícono de play genérico */
.video-play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 68px;
  height: 68px;
  background: rgba(255, 0, 0, 0.9); /* YouTube rojo por defecto */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

/* Ícono de play para TikTok */
.video-play-icon--tiktok {
  background: rgba(0, 0, 0, 0.9); /* Negro para TikTok */
  border: 2px solid white;
}

.video-card:hover .video-play-icon {
  background: rgba(255, 0, 0, 1);
  transform: translate(-50%, -50%) scale(1.15);
}

.video-card:hover .video-play-icon--tiktok {
  background: rgba(0, 0, 0, 1);
  transform: translate(-50%, -50%) scale(1.15);
}

/* Contenido del card */
.video-card__content {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.video-card__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: #e1e7ef;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: pointer;
  transition: color 0.2s ease;
}

.video-card__title:hover {
  color: #ff0000; /* Rojo para YouTube por defecto */
}

/* Título hover para TikTok */
.video-card:has(.video-play-icon--tiktok) .video-card__title:hover {
  color: #ffffff; /* Blanco para TikTok */
}

.video-card__category {
  font-size: 12px;
  font-weight: 600;
  color: #ff0000; /* Rojo para YouTube por defecto */
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Categoría para TikTok */
.video-card:has(.video-play-icon--tiktok) .video-card__category {
  color: #ffffff; /* Blanco para TikTok */
}

.video-card__description {
  font-size: 13px;
  line-height: 1.5;
  color: #9ca3af;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footer con metadata y acciones */
.video-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px 16px;
  border-top: 1px solid #2d333b;
  margin: 0 16px;
  padding-top: 16px;
}

.footer-date {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
}

.footer-country {
  color: #7d8694;
  font-size: 12px;
  font-weight: 400;
}

.footer-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid #2d333b;
  background: #1c2127;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  border-color: #4a5568;
  background: #262c34;
}

.action-btn--active {
  background: white;
  border-color: white;
  color: #1c2127;
}

.action-btn--active:hover {
  background: #f3f4f6;
  border-color: #f3f4f6;
}

.action-btn--copied {
  background: #34d399;
  border-color: #34d399;
  color: white;
}

.action-btn--copied:hover {
  background: #10b981;
  border-color: #10b981;
}

/* Wrapper del botón compartir con tooltip */
.share-btn-wrapper {
  position: relative;
  display: inline-block;
}

/* Tooltip "Enlace copiado" */
.share-tooltip {
  /* Anclado a la derecha, igual que el de favorito */
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #1c2127;
  color: #34d399;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid #34d399;
  z-index: 100;
  pointer-events: none;
}

/* Flecha del tooltip */
.share-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 9px;
  border: 6px solid transparent;
  border-top-color: #34d399;
}

/* Animación del tooltip */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.2s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}

/* Wrapper del botón favorito con tooltip */
.favorite-btn-wrapper {
  position: relative;
  display: inline-block;
}

/* Tooltip de favorito */
.favorite-tooltip {
  /* Anclado a la derecha: centrado se sale de la pantalla en móvil */
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: #1c2127;
  color: #fbbf24;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid #fbbf24;
  z-index: 100;
  pointer-events: none;
}

/* Flecha del tooltip favorito */
.favorite-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 9px;
  border: 6px solid transparent;
  border-top-color: #fbbf24;
}

.share-tooltip.tooltip-fade-enter,
.share-tooltip.tooltip-fade-leave-to,
.favorite-tooltip.tooltip-fade-enter,
.favorite-tooltip.tooltip-fade-leave-to {
  transform: translateY(-4px);
}

/* Card leído - estilo más suave */
.video-card--read {
  opacity: 0.75;
}

.video-card--read:hover {
  opacity: 1;
}

/* Responsive design */
@media (max-width: 768px) {
  .video-card {
    min-height: 400px;
  }

  .video-card__thumbnail {
    height: 180px;
  }

  .video-card__title {
    font-size: 15px;
  }

  .video-card__description {
    font-size: 12px;
  }

  .video-card__footer {
    margin: 0 12px;
    padding: 12px 12px;
  }

  .footer-date {
    font-size: 12px;
  }

  .action-btn {
    width: 32px;
    height: 32px;
  }

  .video-play-icon {
    width: 56px;
    height: 56px;
  }

  .video-play-icon svg {
    width: 40px;
    height: 40px;
  }
}
</style>
